import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

import type { MoveRow } from './stock'

const api = clientFor('/wms/api')

/** 库存汇总行：仓库 + 商品 + 批次 聚合后的数量 */
export interface SummaryRow {
  warehouse_id: number
  warehouse_name: string
  sku_id: number
  sku_code: string
  sku_name: string
  unit: string
  batch_no: string
  expiry_date: string | null
  qty: number
  locked_qty: number
  available: number
}

export type DocType = 'asn' | 'order' | 'wave' | 'stocktake'

/** 单据导出的一行：四种单据同构（单号/状态/仓库/行数/创建人/时间） */
export interface DocRow {
  type: DocType
  type_label: string
  id: number
  doc_no: string
  status: string
  status_label: string
  warehouse_id: number
  warehouse_name: string
  item_count: number
  remark: string
  created_by: string
  created_at: string
}

export interface SummaryQuery extends PageQuery {
  warehouse?: number
  keyword?: string
}

export interface MovesQuery extends PageQuery {
  warehouse?: number
  move_type?: string
  keyword?: string
  from?: string
  to?: string
}

export interface DocQuery extends PageQuery {
  type?: DocType | ''
  warehouse?: number
  status?: string
  keyword?: string
}

/** 与后端 services/wms/internal/report/model.go 的 DocTypeNames 同源两份，改一边必须改另一边 */
export const DOC_TYPE_LABELS: Record<DocType, string> = {
  asn: '入库单',
  order: '出库单',
  wave: '波次',
  stocktake: '盘点单',
}

export const getStockSummary = (q: SummaryQuery): Promise<PageResult<SummaryRow>> =>
  api.get<PageResult<SummaryRow>>('/reports/stock-summary', { ...q })

export const getReportMoves = (q: MovesQuery): Promise<PageResult<MoveRow>> =>
  api.get<PageResult<MoveRow>>('/reports/moves', { ...q })

export const getReportDocuments = (q: DocQuery): Promise<PageResult<DocRow>> =>
  api.get<PageResult<DocRow>>('/reports/documents', { ...q })

/**
 * 导出 CSV：同一个路径带 export=csv，后端直接回 UTF-8 BOM 的附件。
 * 返回 Blob，由 saveBlob 落地（Content-Disposition 里的文件名拿不到，前端自己拼）。
 */
export const exportStockSummary = (q: Omit<SummaryQuery, 'page' | 'size'>): Promise<Blob> =>
  api.getBlob('/reports/stock-summary', { ...q, export: 'csv' })

export const exportMoves = (q: Omit<MovesQuery, 'page' | 'size'>): Promise<Blob> =>
  api.getBlob('/reports/moves', { ...q, export: 'csv' })

export const exportDocuments = (q: Omit<DocQuery, 'page' | 'size'>): Promise<Blob> =>
  api.getBlob('/reports/documents', { ...q, export: 'csv' })
